<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
      </div>
      <div class="columns">
        <div class="column" style="position: relative;">
          <b-loading :is-full-page="false" :active="isLoading" />
          <b-field grouped>
            <b-field label="Fiscal Year">
              <b-input v-model="fiscalYear" type="text" :disabled="isSaving" />
            </b-field>
            <b-field label="Inventory Number">
              <b-input v-model="selectedInventory" type="text" :disabled="isSaving" />
            </b-field>
            <b-field>
              <b-button
                type="is-success"
                icon-left="save"
                :loading="isLoading"
                :disabled="isLoading || isSaving"
                @click="load"
              >
                Load
              </b-button>
            </b-field>
          </b-field>
          <b-message v-if="loadError" type="is-danger" has-icon :closable="false">
            {{ loadError }}
          </b-message>
          <b-field label="Plant">
            <div v-if="selectedPlant">
              {{ selectedPlant.id + ' - ' + selectedPlant.name }}
            </div>
          </b-field>
          <p v-if="isLoaded" class="mb-3">
            {{ inventories.length }} line(s), {{ countedCount }} already counted in SAP.
          </p>
          <b-field v-for="(inventory, index) in inventories" :key="index + 'inventory'">
            <b-field style="background-color: white; align-items: center; display: flex; padding: 0.1rem;" class="ml-2">
              <div style="margin-right: 10px;">
                {{ "Material Name:" }}
              </div>
              <div style="margin-right: 10px;">
                {{ inventory.MatName }}
              </div>
            </b-field>
            <b-field style="background-color: white; align-items: center; display: flex; padding: 0.1rem;" class="ml-2">
              <div style="margin-right: 10px;">
                {{ "Material N°:" }}
              </div>
              <div style="margin-right: 10px;">
                {{ inventory.Material }}
              </div>
            </b-field>
            <b-field class="ml-2" :type="invalidItems.has(inventory.Item) ? 'is-danger' : ''">
              <b-input
                v-model="inventory.qty"
                type="number"
                :disabled="inventory.Counted || isSaving"
                min="0"
                step="any"
              />
            </b-field>
            <b-field style="height: 100%; align-items: center; display: flex; margin-left: 0.3rem;">
              <div style="margin-right: 10px;">
                {{ "Zero?" }}
              </div>
              <b-checkbox v-model="inventory.zero" name="zeroCrates" :disabled="inventory.Counted || isSaving" />
            </b-field>
          </b-field>
          <b-field :message="needsReload ? 'Reload the inventory before saving again, so lines that may already be in SAP are not sent twice.' : ''" :type="needsReload ? 'is-danger' : ''">
            <b-button
              type="is-success"
              icon-left="save"
              :disabled="isLoading || isSaving || !isLoaded || needsReload"
              @click="openReview"
            >
              Save
            </b-button>
          </b-field>
        </div>
      </div>
      <b-modal
        :active.sync="isValidating"
        scroll="keep"
        has-modal-card
        :can-cancel="isSaving ? false : ['escape', 'x', 'outside']"
      >
        <form v-if="isValidating" @submit.prevent="saveInventory">
          <div class="modal-card" style="min-width: 50vw; width: fit-content; height: fit-content;">
            <header class="modal-card-head">
              <p class="modal-card-title">
                {{ modalTitle }}
              </p>
            </header>
            <section class="modal-card-body">
              <!-- Review before anything is sent -->
              <template v-if="!save.phase">
                <b-message v-if="review.invalid.length" type="is-danger" title="Fix these lines before submitting" :closable="false">
                  <ul>
                    <li v-for="line in review.invalid" :key="'invalid' + line.inventory.Item">
                      <strong>{{ lineLabel(line.inventory) }}</strong>: {{ line.reason }}
                    </li>
                  </ul>
                </b-message>
                <template v-if="review.send.length">
                  <p class="mb-2">
                    <strong>{{ review.send.length }} line(s) will be sent to SAP.</strong>
                    Submitted inventories cannot be modified.
                  </p>
                  <ul class="mb-4 report-list">
                    <li v-for="line in review.send" :key="'send' + line.inventory.Item">
                      {{ lineLabel(line.inventory) }}: <strong>{{ sentText(line) }}</strong>
                    </li>
                  </ul>
                </template>
                <p v-else-if="!review.invalid.length" class="mb-4">
                  <strong>Nothing to send:</strong> no line has a quantity or a zero count.
                </p>
                <b-message v-if="review.skipped.length" type="is-warning" :closable="false">
                  <p class="mb-2">
                    <strong>{{ review.skipped.length }} line(s) have no quantity and will NOT be sent.</strong>
                    Tick "Zero?" to post a zero count.
                  </p>
                  <ul class="report-list">
                    <li v-for="line in review.skipped" :key="'skip' + line.inventory.Item">
                      {{ lineLabel(line.inventory) }}
                    </li>
                  </ul>
                </b-message>
              </template>

              <!-- Sending -->
              <template v-else-if="save.phase === 'sending'">
                <p class="mb-2">
                  <strong>Sending lines {{ batchStart }}–{{ batchEnd }} of {{ save.total }} to SAP</strong>
                  (batch {{ save.batch }} of {{ save.batchCount }})…
                </p>
                <b-progress
                  :value="save.done"
                  :max="save.total"
                  type="is-info"
                  size="is-medium"
                  show-value
                >
                  {{ save.done }} / {{ save.total }} lines
                </b-progress>
                <p class="help">
                  Elapsed: {{ elapsedText }}. Do not close or refresh this page until it finishes.
                </p>
              </template>

              <!-- Checking SAP -->
              <template v-else-if="save.phase === 'verifying'">
                <p class="mb-2">
                  <strong>Reloading the inventory from SAP to check every line was recorded…</strong>
                </p>
                <b-progress type="is-info" size="is-medium" />
                <p class="help">
                  Elapsed: {{ elapsedText }}. Do not close or refresh this page until it finishes.
                </p>
              </template>

              <!-- Result -->
              <template v-else-if="report">
                <b-message :type="report.type" :closable="false">
                  <strong>{{ report.headline }}</strong>
                  <p v-if="save.verifyError" class="mt-2">
                    Could not reload the inventory to double-check the result: {{ save.verifyError }}
                    Reload it before saving again.
                  </p>
                  <p v-if="save.skipped" class="mt-2">
                    {{ save.skipped }} line(s) had no quantity and were not sent.
                  </p>
                </b-message>
                <div v-for="group in report.groups" :key="group.key" class="mb-4">
                  <p class="mb-1">
                    <b-tag :type="group.type">
                      {{ group.lines.length }}
                    </b-tag>
                    <strong>{{ group.title }}</strong>
                  </p>
                  <ul class="report-list">
                    <li v-for="line in group.lines" :key="group.key + line.payload.Item">
                      {{ lineLabel(line.inventory) }}: sent <strong>{{ sentText(line) }}</strong>
                      <span v-if="line.detail" class="has-text-grey-dark"> — {{ line.detail }}</span>
                    </li>
                  </ul>
                </div>
              </template>
            </section>
            <footer class="modal-card-foot">
              <button
                v-if="!save.phase"
                type="submit"
                class="button is-info is-fullwidth"
                :disabled="review.invalid.length > 0 || review.send.length === 0"
              >
                {{ $t('global.submit') }}
              </button>
              <button v-else-if="isSaving" type="button" class="button is-info is-fullwidth is-loading" disabled>
                {{ $t('global.submit') }}
              </button>
              <template v-else>
                <button type="button" class="button" @click="copyReport">
                  Copy details
                </button>
                <button type="button" class="button is-info" @click="isValidating = false">
                  Close
                </button>
              </template>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import sapInventory, { SAVE_BATCH_SIZE } from '../../../../services/v3/sapInventory'
import plantSelector from '@/mixins/plantSelector'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

// Worst outcome first, so what needs attention is at the top of the report.
const RESULT_GROUPS = [
  { key: 'failed', type: 'is-danger', title: 'Not recorded in SAP' },
  { key: 'mismatch', type: 'is-danger', title: 'Recorded in SAP with a different quantity' },
  { key: 'unconfirmed', type: 'is-warning', title: 'Accepted by SAP, but still not marked as counted' },
  { key: 'unknown', type: 'is-warning', title: 'Outcome unknown: reload the inventory to check' },
  { key: 'notSent', type: 'is-warning', title: 'Not sent' },
  { key: 'unverified', type: 'is-info', title: 'Accepted by SAP (could not double-check)' },
  { key: 'saved', type: 'is-success', title: 'Recorded in SAP' }
]

function emptySave () {
  return {
    phase: null, // null (review) | 'sending' | 'verifying' | 'done'
    total: 0,
    done: 0,
    batch: 0,
    batchCount: 0,
    startedAt: 0,
    elapsed: 0,
    skipped: 0,
    lines: [],
    verifyError: null
  }
}

export default {
  name: 'SAPInventory',
  components: {
  },
  mixins: [plantSelector],
  beforeRouteLeave (to, from, next) {
    if (this.isSaving && !window.confirm('The inventory is still being sent to SAP. Leaving now may leave it partially submitted. Leave anyway?')) {
      return next(false)
    }
    next()
  },
  data: function () {
    return {
      isLoading: false,
      isLoaded: false,
      loadError: null,
      needsReload: false,
      inventories: [],
      loaded: null,
      fiscalYear: null,
      selectedInventory: null,
      selectedPlant: null,
      isValidating: false,
      save: emptySave(),
      timer: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    isSaving () {
      return this.save.phase === 'sending' || this.save.phase === 'verifying'
    },
    countedCount () {
      return this.inventories.filter(x => x.Counted).length
    },
    review () {
      const out = { send: [], skipped: [], invalid: [] }
      this.inventories.filter(x => !x.Counted).forEach(inventory => {
        const check = this.checkLine(inventory)
        out[check.status].push({ inventory, ...check })
      })
      return out
    },
    invalidItems () {
      return new Set(this.review.invalid.map(x => x.inventory.Item))
    },
    batchStart () {
      return Math.min((this.save.batch - 1) * SAVE_BATCH_SIZE + 1, this.save.total)
    },
    batchEnd () {
      return Math.min(this.save.batch * SAVE_BATCH_SIZE, this.save.total)
    },
    elapsedText () {
      const minutes = Math.floor(this.save.elapsed / 60)
      const seconds = this.save.elapsed % 60
      return minutes ? `${minutes} min ${seconds} s` : `${seconds} s`
    },
    modalTitle () {
      if (!this.save.phase) return 'Submit inventory to SAP'
      if (this.isSaving) return 'Submitting to SAP…'
      return 'Submission result'
    },
    report () {
      if (this.save.phase !== 'done') return null
      const lines = this.save.lines
      const groups = RESULT_GROUPS
        .map(group => ({ ...group, lines: lines.filter(x => x.final === group.key) }))
        .filter(group => group.lines.length)
      const good = lines.filter(x => x.final === 'saved' || x.final === 'unverified').length
      const allGood = good === lines.length
      return {
        groups,
        type: allGood ? (this.save.verifyError ? 'is-warning' : 'is-success') : 'is-danger',
        headline: allGood
          ? `All ${lines.length} line(s) were accepted by SAP.`
          : `Only ${good} of ${lines.length} line(s) were recorded in SAP. ${lines.length - good} need attention (see below).`
      }
    }
  },
  watch: {
    isValidating (value) {
      if (!value && !this.isSaving) this.save = emptySave()
    }
  },
  mounted: async function () {
    window.addEventListener('beforeunload', this.onBeforeUnload)
    // The plant comes back from SAP as a storage location, which is exactly what
    // the hidden FRZ* plants are, so this lookup needs the unfiltered list.
    await this.loadPlants({ force: true })
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    this.stopTimer()
  },
  methods: {
    resetData () {
      this.inventories = []
      this.loaded = null
      this.selectedPlant = null
      this.isLoaded = false
      this.loadError = null
      this.needsReload = false
      this.isValidating = false
    },
    async fetchInventory (fiscalYear, inventoryNumber) {
      const data = await sapInventory.get(fiscalYear, inventoryNumber)
      if (!data || !data.d) {
        throw new Error(`Inventory ${inventoryNumber} (${fiscalYear}) was not found in SAP.`)
      }
      const details = (data.d.InventoryDetailSet && data.d.InventoryDetailSet.results) || []
      return {
        stgeLoc: data.d.StgeLoc,
        lines: details.map(element => ({
          Physinventory: element.Physinventory,
          Fiscalyear: element.Fiscalyear,
          Item: element.Item,
          Material: element.Material,
          MatName: element.MatName,
          Plant: element.Plant,
          StgeLoc: element.StgeLoc,
          CountUser: element.CountUser,
          CountDate: element.CountDate,
          Counted: element.Counted,
          DiffPosted: element.DiffPosted,
          Recount: element.Recount,
          DeleteInd: element.DeleteInd,
          ZeroCount: element.ZeroCount,
          EntryQnt: element.EntryQnt,
          EntryUom: element.EntryUom,
          qty: parseFloat(element.EntryQnt),
          zero: element.ZeroCount
        }))
      }
    },
    applyInventory (fiscalYear, inventoryNumber, stgeLoc, lines) {
      this.loaded = { fiscalYear, inventoryNumber }
      this.selectedPlant = this.allPlants.find(x => x.id === stgeLoc) || null
      this.inventories = lines
      this.isLoaded = lines.length > 0
    },
    async load () {
      if (!this.selectedInventory) {
        this.$buefy.toast.open({ message: 'Please enter an inventory number', duration: 3000, type: 'is-danger' })
        return
      }
      this.resetData()
      this.isLoading = true
      try {
        const { stgeLoc, lines } = await this.fetchInventory(this.fiscalYear, this.selectedInventory)
        this.applyInventory(this.fiscalYear, this.selectedInventory, stgeLoc, lines)
        if (lines.length) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.loadError = `Inventory ${this.selectedInventory} was found in SAP but has no lines.`
        }
      } catch (error) {
        this.loadError = `Could not load the inventory: ${error.message}`
        this.$buefy.toast.open({ message: this.loadError, duration: 8000, type: 'is-danger' })
      } finally {
        this.isLoading = false
      }
    },
    // What would be sent for an uncounted line: { status: 'send' | 'skipped' | 'invalid' }.
    checkLine (inventory) {
      const raw = inventory.qty
      const empty = raw === '' || raw === null || raw === undefined || (typeof raw === 'number' && isNaN(raw))
      const qty = empty ? null : Number(raw)
      if (!empty && isNaN(qty)) {
        return { status: 'invalid', reason: `"${raw}" is not a valid quantity` }
      }
      if (qty < 0) {
        return { status: 'invalid', reason: 'the quantity cannot be negative' }
      }
      if (inventory.zero) {
        // The API drops the quantity of a zero count, so this used to post 0 silently.
        if (qty > 0) {
          return { status: 'invalid', reason: `quantity ${qty} is entered but "Zero?" is ticked; clear one of them` }
        }
        return { status: 'send', payload: this.payload(inventory, true, null) }
      }
      if (empty || qty === 0) {
        return { status: 'skipped' }
      }
      return { status: 'send', payload: this.payload(inventory, false, String(qty)) }
    },
    payload (inventory, zeroCount, entryQnt) {
      return {
        Physinventory: inventory.Physinventory,
        Fiscalyear: inventory.Fiscalyear,
        Item: inventory.Item,
        Material: inventory.Material,
        ZeroCount: zeroCount,
        EntryQnt: entryQnt
      }
    },
    lineLabel (inventory) {
      return `${inventory.MatName} (${inventory.Material}, item ${inventory.Item})`
    },
    sentText (line) {
      return line.payload.ZeroCount ? 'zero count' : `${line.payload.EntryQnt} ${line.inventory.EntryUom || ''}`.trim()
    },
    openReview () {
      if (!this.inventories.some(x => !x.Counted)) {
        this.$buefy.toast.open({ message: 'Every line of this inventory is already counted in SAP.', duration: 4000, type: 'is-warning' })
        return
      }
      this.save = emptySave()
      this.isValidating = true
    },
    async saveInventory () {
      if (this.isSaving) return
      const { send, skipped, invalid } = this.review
      if (invalid.length || !send.length) return
      const lines = send.map(x => ({ inventory: x.inventory, payload: x.payload, status: 'pending', error: null, final: null, detail: null }))
      this.save = {
        ...emptySave(),
        phase: 'sending',
        total: lines.length,
        batch: 1,
        batchCount: Math.ceil(lines.length / SAVE_BATCH_SIZE),
        startedAt: Date.now(),
        skipped: skipped.length,
        lines
      }
      this.startTimer()
      try {
        // Batches go one at a time: SAP locks the inventory document while it posts.
        for (let i = 0; i < lines.length; i += SAVE_BATCH_SIZE) {
          const batch = lines.slice(i, i + SAVE_BATCH_SIZE)
          this.save.batch = i / SAVE_BATCH_SIZE + 1
          let stop = false
          try {
            const data = await sapInventory.saveInventory(batch.map(x => x.payload))
            this.applyBatchResults(batch, data && data.results)
          } catch (error) {
            if (error.data && Array.isArray(error.data.results)) {
              this.applyBatchResults(batch, error.data.results)
            } else {
              // Timeout, network or SAP session failure: which lines of this batch reached
              // SAP is unknown until the reload below. Stop rather than pile more requests
              // onto a document SAP may still be posting.
              batch.forEach(x => { x.status = 'unknown'; x.error = error.message })
              stop = true
            }
          }
          this.save.done = i + batch.length
          if (stop) break
        }
        lines.filter(x => x.status === 'pending').forEach(x => {
          x.status = 'notSent'
          x.error = 'submission stopped after an earlier error'
        })
        this.save.phase = 'verifying'
        await this.verifySave()
      } catch (error) {
        // Unexpected bug in this page: never leave the modal stuck on the progress bar.
        this.save.verifyError = error.message
        this.needsReload = true
        lines.filter(x => !x.final).forEach(x => { x.final = 'unknown'; x.detail = x.error })
      } finally {
        this.stopTimer()
        this.save.phase = 'done'
      }
    },
    applyBatchResults (batch, results) {
      const byItem = new Map((results || []).map(r => [r.Item, r]))
      batch.forEach(x => {
        const result = byItem.get(x.payload.Item)
        if (!result) {
          x.status = 'unknown'
          x.error = 'the server did not report a result for this line'
        } else if (result.ok) {
          x.status = 'accepted'
        } else {
          x.status = 'rejected'
          x.error = result.error || 'rejected by SAP'
        }
      })
    },
    // SAP is the source of truth: reload the document and classify every sent line
    // by what SAP now holds, not by what the requests reported.
    async verifySave () {
      const lines = this.save.lines
      const typed = new Map(this.inventories.map(x => [x.Item, { qty: x.qty, zero: x.zero }]))
      let fresh
      try {
        fresh = await this.fetchInventory(this.loaded.fiscalYear, this.loaded.inventoryNumber)
      } catch (error) {
        this.save.verifyError = error.message
        this.needsReload = true
        lines.forEach(x => {
          if (x.status === 'accepted') {
            x.final = 'unverified'
            x.inventory.Counted = true
          } else {
            x.final = { rejected: 'failed', unknown: 'unknown', notSent: 'notSent' }[x.status]
            x.detail = x.error
          }
        })
        return
      }
      const byItem = new Map(fresh.lines.map(x => [x.Item, x]))
      lines.forEach(x => {
        const now = byItem.get(x.payload.Item)
        if (now && now.Counted) {
          const sapQty = parseFloat(now.EntryQnt) || 0
          const expected = x.payload.ZeroCount ? 0 : Number(x.payload.EntryQnt)
          if (Math.abs(sapQty - expected) < 1e-6) {
            x.final = 'saved'
            if (x.error) x.detail = `the request reported "${x.error}", but SAP now holds this count`
          } else {
            x.final = 'mismatch'
            x.detail = `SAP now holds ${sapQty} ${now.EntryUom || ''}`.trim() + (x.error ? ` (request reported: ${x.error})` : '')
          }
        } else if (x.status === 'accepted') {
          x.final = 'unconfirmed'
          x.detail = now ? 'SAP answered OK, but the line is still open after reloading' : 'the line is no longer in the inventory'
        } else if (x.status === 'unknown') {
          // The request may still be finishing on the server: resubmitting now could post twice.
          x.final = 'unknown'
          x.detail = `${x.error}; not counted yet when reloaded`
          this.needsReload = true
        } else {
          x.final = x.status === 'notSent' ? 'notSent' : 'failed'
          x.detail = x.error
        }
      })
      // Keep what was typed on lines that are still open, so they can be fixed and resent.
      fresh.lines.forEach(x => {
        const entry = typed.get(x.Item)
        if (!x.Counted && entry) {
          x.qty = entry.qty
          x.zero = entry.zero
        }
      })
      this.applyInventory(this.loaded.fiscalYear, this.loaded.inventoryNumber, fresh.stgeLoc, fresh.lines)
    },
    async copyReport () {
      const header = `SAP inventory ${this.loaded ? this.loaded.inventoryNumber : ''} / ${this.loaded ? this.loaded.fiscalYear : ''}, submitted ${new Date(this.save.startedAt).toLocaleString()}`
      const rows = this.save.lines.map(x => [
        x.payload.Item,
        x.payload.Material,
        x.inventory.MatName,
        this.sentText(x),
        x.final,
        x.detail || ''
      ].join('\t'))
      const text = [header, this.save.verifyError ? `Verification failed: ${this.save.verifyError}` : '', 'Item\tMaterial\tName\tSent\tResult\tDetail', ...rows]
        .filter(Boolean)
        .join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.$buefy.toast.open({ message: 'Details copied to the clipboard', duration: 3000, type: 'is-success' })
      } catch (error) {
        this.$buefy.toast.open({ message: `Could not copy to the clipboard: ${error.message}`, duration: 5000, type: 'is-danger' })
      }
    },
    startTimer () {
      this.stopTimer()
      this.timer = setInterval(() => {
        this.save.elapsed = Math.round((Date.now() - this.save.startedAt) / 1000)
      }, 1000)
    },
    stopTimer () {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
    onBeforeUnload (event) {
      if (this.isSaving) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
  }
}

</script>

<style scoped>
.report-list {
  max-height: 12rem;
  overflow-y: auto;
  list-style: disc;
  padding-left: 1.5rem;
}
</style>
